import type { Subscription } from 'rxjs';
import { Subject, fromEvent, merge, of, timer } from 'rxjs';
import {
  filter,
  map,
  scan,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs/operators';

import { isInputElement } from '@/util';

import { normalizeKeyCode } from '../util';

type ShortcutAction = {
  action: () => void;
  preventDefault: boolean;
  sequence: string[];
};

type ShortcutBuffer = {
  action: Nullish<ShortcutAction>;
  event: Nullish<KeyboardEvent>;
  sequence: string[];
  sequenceStr: string;
};

export class ShortcutService {
  private _commandMap = new Map<string, ShortcutAction>();
  private _actionSubscription!: Subscription;
  private _reset$ = new Subject<void>();
  static CHORD_TIMEOUT = 500;
  static THROTTLE_TIME = 32;

  constructor(public debug = false) {
    this.initialize();
  }

  private initialize() {
    const keydown$ = fromEvent<KeyboardEvent>(window, 'keydown').pipe(
      filter(e => !isInputElement(e.target)),
      map(e => ({ event: e, code: normalizeKeyCode(e) })),
      filter(({ code }) => code !== null),
    );

    const action$ = merge(
      keydown$,
      this._reset$.pipe(map(() => ({ event: null, code: 'RESET' }))),
    );

    this._actionSubscription = action$
      .pipe(
        throttleTime(ShortcutService.THROTTLE_TIME),
        scan((buffer: ShortcutBuffer, { event, code }) => {
          if (code === 'RESET') {
            return this._getDefaultBuffer();
          }

          /* v8 ignore next -- @preserve */
          if (buffer.sequence.length > 0 && event?.repeat) {
            return buffer;
          }

          const nextBuffer = [...buffer.sequence, code!];
          const sequenceStr = nextBuffer.join(' ');
          const shortcutAction = this._commandMap.get(sequenceStr);
          const hasPrefix = Array.from(this._commandMap.keys()).some(k =>
            k.startsWith(sequenceStr + ' '),
          );

          if (shortcutAction && hasPrefix) {
            event?.preventDefault();

            /* v8 ignore next -- @preserve */
            this.debug &&
              console.log(
                `[ShortcutService.ChordWaiting] ${nextBuffer.join(' -> ')} ...`,
              );

            return {
              action: shortcutAction,
              event,
              sequence: nextBuffer,
              sequenceStr,
            };
          }

          if (shortcutAction) {
            this.executeShortcut(shortcutAction, event, sequenceStr);

            return this._getDefaultBuffer();
          }

          if (hasPrefix) {
            event?.preventDefault();

            /* v8 ignore next -- @preserve */
            this.debug &&
              console.log(
                `[ShortcutService.ChordWaiting] ${nextBuffer.join(' -> ')} ...`,
              );

            return {
              action: null,
              event: null,
              sequence: nextBuffer,
              sequenceStr,
            };
          }

          return this._getDefaultBuffer();
        }, this._getDefaultBuffer()),
        switchMap(buffer => {
          if (buffer.sequence.length > 0) {
            return timer(ShortcutService.CHORD_TIMEOUT).pipe(
              tap(() => {
                if (buffer.action) {
                  this.executeShortcut(
                    buffer.action,
                    buffer.event,
                    buffer.sequenceStr,
                  );
                }

                /* v8 ignore next -- @preserve */
                this.debug &&
                  console.log('[ShortcutService.Timeout] Chord Cancelled');

                this._reset$.next();
              }),
            );
          }

          return of(null);
        }),
      )
      .subscribe();
  }

  private executeShortcut(
    shortcutAction: ShortcutAction,
    event: Nullish<KeyboardEvent>,
    sequenceStr: string,
  ) {
    if (shortcutAction.preventDefault) event?.preventDefault();

    /* v8 ignore next -- @preserve */
    this.debug &&
      console.log(`[ShortcutService.ShortcutExecuted] ${sequenceStr}`);
    shortcutAction.action();
  }

  private _getDefaultBuffer = (): ShortcutBuffer => ({
    action: null,
    event: null,
    sequence: [],
    sequenceStr: '',
  });

  cleanup() {
    this._actionSubscription.unsubscribe();
  }

  register(sequence: string[], action: () => void, preventDefault = true) {
    const normalizedSequence = sequence.reduce((acc, key) => {
      const splited = key.split(/(?<!alt|ctrl|cmd|shift)\+/g);

      acc.push(...splited);

      return acc;
    }, [] as string[]);
    const key = normalizedSequence.join(' ').toLowerCase();

    this._commandMap.set(key, { sequence, action, preventDefault });
  }
}
